import React, {ReactNode, useContext, useEffect, useMemo, useState} from 'react';
import axios, {AxiosInstance} from 'axios';
import {useOidcUser} from "../auth/OidcUserProvider";

export const AxiosContext = React.createContext<AxiosInstance | undefined>(undefined);

export function useAxios(): AxiosInstance {
  return useContext(AxiosContext)!!;
}

/**
 * Header the account service sets when the roles of the access token differ from the stored ones
 * (e.g. after the roles of the current user were changed). The user has to sign in again then.
 */
export const OUTDATED_PERMISSIONS_HEADER = 'x-outdated-permissions';

const OutdatedPermissionsContext = React.createContext<boolean>(false);

export function useOutdatedPermissions(): boolean {
  return useContext(OutdatedPermissionsContext);
}

export function AxiosProvider(props: {
  children: ReactNode
}) {
  const oidcUser = useOidcUser();
  const accessToken = oidcUser?.access_token;
  const [outdatedPermissions, setOutdatedPermissions] = useState(false);

  const axiosInstance = useMemo<AxiosInstance | undefined>(() => {
    const newInstance = axios.create();
    newInstance.defaults.headers.post['Content-Type'] = 'application/json';
    newInstance.defaults.headers.put['Content-Type'] = 'application/json';
    newInstance.defaults.headers.get['Content-Type'] = 'application/json';
    newInstance.defaults.headers.post.Accept = 'application/json';
    newInstance.defaults.headers.put.Accept = 'application/json';
    newInstance.defaults.headers.get.Accept = 'application/json';
    newInstance.interceptors.response.use(response => {
      if (response.headers?.[OUTDATED_PERMISSIONS_HEADER] === 'true') {
        setOutdatedPermissions(true);
      }
      return response;
    });
    if (accessToken) {
      console.log('Set bearer token');
      newInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      return newInstance;
    } else {
      return undefined;
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (accessToken) {
      console.info('access token changed => setting axios defaults');
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      if (axiosInstance)
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }
  }, [accessToken, axiosInstance]);

  if (!axiosInstance) {
    console.log('No axiosInstance');
  } else {
    console.log('Got axiosInstance');
  }
  return (
    <AxiosContext.Provider value={axiosInstance}>
      <OutdatedPermissionsContext.Provider value={outdatedPermissions}>
        {axiosInstance && props.children}
      </OutdatedPermissionsContext.Provider>
    </AxiosContext.Provider>
  );
}
